import { useSelector } from 'react-redux';
import { NA } from 'modules/experiments/constants';
import {
  getExperimentHasWinner,
  getExperimentStateStatus,
} from 'modules/experiments/ducks/selectors';
import { useExperimentsTranslations } from 'modules/experiments/hooks';
import { useVariantTableConfidenceLevelContext } from 'modules/experiments/contexts';
import { CellAlignTypes, ExperimentSummaryRowDataProps } from 'modules/experiments/types';
import {
  calculateLift,
  calculateLiftInterval,
  getCellDisplayType,
  CELL_DISPLAY_TYPE,
} from 'modules/experiments/utils';
import { colors } from 'ui/theme';
import { RowCell } from './RowCell';
import { SecondaryContent, StyledLiftPrimaryText } from './Styled';

/**
 * Checks if variant is control and returns gray.
 * If variant is not control it will check the experiment state to determine if the cell value shoudl be colorized
 * Experiment state with RUNNING and READY should not display color, and only FINISHED state will
 *
 * @param {boolean.isControl} - isControl value of object in the variations array (See analytics data model)
 * @param {CELL_DISPLAY_TYPE.cellDisplayType} - Type depends on state of experiment see getCellDisplayType
 * @param {number.lift} - lift calculated lift value for variant
 * @return {string}  returns grey/red/green depending on if variant is control and the experiment running state
 */
export const getLiftTextColor = (
  isControl: boolean,
  cellDisplayType: CELL_DISPLAY_TYPE,
  lift: number,
): string => {
  let styledTextColor = colors.textPrimary;
  if (!isControl) {
    styledTextColor = lift > 0 ? colors.green100 : colors.red100;
  }
  return styledTextColor;
};

export const LiftCell = ({ row }: ExperimentSummaryRowDataProps) => {
  const { t } = useExperimentsTranslations();
  const { isConfidenceLevelToggleActive } = useVariantTableConfidenceLevelContext();
  const experimentHasWinner = useSelector(getExperimentHasWinner);
  const isMetricsAvailableForCalculations =
    row.sends &&
    row.experimentWinningCriteria &&
    row.controlExperimentSends &&
    row.controlExperimentWinningCriteria;
  const state = useSelector(getExperimentStateStatus);
  const cellDisplayType = getCellDisplayType(state, experimentHasWinner);
  // Metric Calculations
  const lift = calculateLift({
    variantSends: row.sends || 0,
    variantExperimentWinningCriteria: row.experimentWinningCriteria,
    controlExperimentSends: row.controlExperimentSends,
    controlExperimentWinningCriteria: row.controlExperimentWinningCriteria,
  });
  const liftInterval = calculateLiftInterval({
    variantSends: row.sends || 0,
    variantExperimentWinningCriteria: row.experimentWinningCriteria,
    controlExperimentSends: row.controlExperimentSends,
    controlExperimentWinningCriteria: row.controlExperimentWinningCriteria,
  });
  const styledTextColor = getLiftTextColor(row.control, cellDisplayType, lift);

  // Display Texts
  const liftBoundMin = (lift - liftInterval).toFixed(2);
  const liftBoundMax = (lift + liftInterval).toFixed(2);
  const displayLift = lift.toFixed(2);
  const displayLiftInterval = liftInterval.toFixed(2);
  const liftText = isConfidenceLevelToggleActive
    ? `${displayLift}pp ± ${displayLiftInterval}`
    : `${displayLift}pp`;
  const liftSecondaryText = isConfidenceLevelToggleActive
    ? `[ ${liftBoundMin}, ${liftBoundMax} ]`
    : '';

  if (!isMetricsAvailableForCalculations || cellDisplayType === CELL_DISPLAY_TYPE.NA) {
    return (
      <RowCell
        isWinner={row.winner.toString()}
        dataTestId={`lift-cell-na-${row.variantTemplateID}`}
        primaryText={NA}
        secondaryText={isConfidenceLevelToggleActive ? NA : ''}
        align={CellAlignTypes.flexEnd}
      />
    );
  }

  if (row.control) {
    return (
      <RowCell
        isWinner={row.winner.toString()}
        dataTestId={`lift-cell-control-${row.variantTemplateID}`}
        primaryText={t('variantTable.cell.baseline')}
        greyOutContent={true}
        align={CellAlignTypes.flexEnd}
      />
    );
  }

  return (
    <RowCell
      isWinner={row.winner.toString()}
      dataTestId={`lift-cell-variant-${row.variantTemplateID}`}
      primaryText={liftText}
      secondaryText={liftSecondaryText}
      align={CellAlignTypes.flexEnd}
    >
      <StyledLiftPrimaryText
        data-test={`lift-cell-primary-text-${row.variantTemplateID}`}
        color={styledTextColor}
      >
        {liftText}
      </StyledLiftPrimaryText>
      <SecondaryContent data-test={`lift-cell-secondary-text-${row.variantTemplateID}`}>
        {liftSecondaryText}
      </SecondaryContent>
    </RowCell>
  );
};
